// Hook custom per recuperare le frazioni di un comune tramite API.
// Nota: uso axios e TypeScript per tipizzare la risposta e gestire errori e loading.

import { useState, useEffect } from "react";
import axios from "axios";

// Prendo la base URL dell'API dalle variabili d'ambiente
const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

// Tipizzazione di una singola frazione
interface Frazione {
    id: number;
    comuneIstat: string;
    comune: string | null;
    provincia: string | null;
    frazione: string;
    seoString: string | null;
}

/**
 * useGetFrazioni
 * Recupera le frazioni di un comune a partire dal codice ISTAT del comune
 *
 * @remarks
 * - `frazioni` è un array di oggetti Frazione
 * - `loading` è true mentre la chiamata è in corso
 * - `error` contiene un messaggio di errore in caso di fallimento
 */
export const useGetFrazioni = (comuneIstat: string) => {
    const [frazioni, setFrazioni] = useState<Frazione[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!comuneIstat) {
            setLoading(false);
            return;
        }

        (async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await axios.post<Frazione[]>(
                    `${API_BASE_URL}/frazioni`,
                    { comuneIstat }
                );

                setFrazioni(response.data);
            } catch (err) {
                console.error("Errore in useGetFrazioni:", err);
                setError("Impossibile recuperare le frazioni");
            } finally {
                setLoading(false);
            }
        })();
    }, [comuneIstat]);

    return { frazioni, loading, error };
};

export default useGetFrazioni;